// src/components/tools/CommandRail.jsx
//
// Vertical command rail under the Compare/Flicker mini-rail. Two tools:
//   - Measure — engagement is driven by useMeasure().active. Its slide-out is
//     a horizontal pill (MeasureToolPanel) anchored to the right of the
//     button; it only appears once `deriveSlideoutContent` returns content.
//   - Fly To — engagement uses the shared useToolsUiStore.open + activeTab;
//     its slide-out is the existing FlyToToolPanel.
//
// Tools are mutually exclusive: engaging one auto-exits the other. ESC exits
// whichever is currently engaged (single global owner of that behavior).

import { useEffect, useRef } from "react";
import { Ruler, Navigation, PenLine } from "lucide-react";
import { useToolsUiStore } from "../../stores/useToolsUiStore";
import { useAnnotationToolStore } from "../../stores/useAnnotationToolStore";
import MeasureToolPanel from "./MeasureToolPanel";
import { deriveSlideoutContent } from "./measureSlideoutContent";
import FlyToToolPanel from "./FlyToToolPanel";

function cx(...c) { return c.filter(Boolean).join(" "); }

function RailButton({ icon, label, isActive, onClick }) {
  // Local capitalized binding so JSX recognizes it as a component; the
  // destructure-rename form trips no-unused-vars in this project's config.
  const Icon = icon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      title={label}
      className={cx(
        "group relative w-[29px] h-[29px] flex items-center justify-center",
        "rounded-sm border transition shadow-md",
        isActive
          ? "border-accent-action/60 text-accent-action"
          : "bg-white/95 border-slate-300 text-slate-700 hover:bg-white"
      )}
    >
      <Icon size={15} />
      <span
        className="absolute left-full ml-2 px-2 py-1
                   bg-bg-soft border border-slate-700 rounded
                   text-[10px] text-slate-200 whitespace-nowrap
                   opacity-0 group-hover:opacity-100
                   pointer-events-none transition-opacity duration-100
                   shadow-lg z-50"
      >
        {label}
      </span>
    </button>
  );
}

export default function CommandRail({ measure, onFlyToPlace, onFlyToBounds, annotationsEnabled = false }) {
  const open = useToolsUiStore((s) => s.open);
  // PadRekko Annotations tool — only present inside a case (annotationsEnabled).
  // Active while the menu is open OR a draw is in progress.
  const annotationsEngaged = useAnnotationToolStore((s) => s.menuOpen || s.drawType !== null);
  const toggleAnnotations = useAnnotationToolStore((s) => s.toggle);
  const activeTab = useToolsUiStore((s) => s.activeTab);
  const setActiveTab = useToolsUiStore((s) => s.setActiveTab);
  const openPanel = useToolsUiStore((s) => s.openPanel);
  const closePanel = useToolsUiStore((s) => s.closePanel);

  const measureActive = measure.active;
  const flytoEngaged = open && activeTab === "flyto";

  function toggleMeasure() {
    if (measureActive) { measure.exit(); return; }
    if (flytoEngaged) closePanel();
    measure.start();
  }

  function toggleFlyTo() {
    if (flytoEngaged) { closePanel(); return; }
    if (measureActive) measure.exit();
    setActiveTab("flyto");
    openPanel();
  }

  // ESC exits whichever tool is engaged. Refs prevent unnecessary re-binds
  // every time downstream measure state (vertexCount, liveStats) changes.
  const measureRef = useRef(measure);
  const closeRef = useRef(closePanel);
  useEffect(() => { measureRef.current = measure; });
  useEffect(() => { closeRef.current = closePanel; });

  useEffect(() => {
    if (!measureActive && !flytoEngaged) return;
    function onKey(e) {
      if (e.key !== "Escape") return;
      if (measureActive) measureRef.current.exit();
      if (flytoEngaged) closeRef.current();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [measureActive, flytoEngaged]);

  const measureContent = deriveSlideoutContent(measure);
  const showMeasurePill = measureActive && measureContent !== null;

  return (
    <>
      {/* Rail */}
      <div className="absolute top-[215px] left-[10px] z-[80] pointer-events-auto flex flex-col gap-1">
        <RailButton
          icon={Ruler}
          label="Measure"
          isActive={measureActive}
          onClick={toggleMeasure}
        />
        <RailButton
          icon={Navigation}
          label="Fly To"
          isActive={flytoEngaged}
          onClick={toggleFlyTo}
        />
        {annotationsEnabled && (
          <RailButton
            icon={PenLine}
            label="Annotations"
            isActive={annotationsEngaged}
            onClick={toggleAnnotations}
          />
        )}
      </div>

      {/* Measure pill — only when there's content to show. */}
      {showMeasurePill && (
        <div className="absolute top-[215px] left-[48px] z-[80] pointer-events-auto">
          <MeasureToolPanel content={measureContent} onClear={measure.clear} />
        </div>
      )}

      {/* FlyTo pill — aligned with the FlyTo button row. The pill owns its
          own border/bg/shadow, so the wrapper is positioning-only. */}
      {flytoEngaged && (
        <div className="absolute top-[248px] left-[48px] z-[80] pointer-events-auto">
          <FlyToToolPanel onFlyToPlace={onFlyToPlace} onFlyToBounds={onFlyToBounds} onClose={closePanel} />
        </div>
      )}
    </>
  );
}
